import clsx from 'clsx';
import { NavLink, useLocation } from 'react-router-dom';

import { appModules, moduleSections, type AppModule } from '@/app/modules';
import { appwriteConfig } from '@/config/appwrite';
import { Icon } from '@/components/ui';

import styles from './Sidebar.module.css';

interface SidebarProps {
  open: boolean;
  onNavigate: () => void;
}

function isActive(module: AppModule, pathname: string): boolean {
  if (module.path === '/') return pathname === '/';
  if (module.matchPrefix) return pathname === module.path || pathname.startsWith(`${module.path}/`);
  return pathname === module.path;
}

export function Sidebar({ open, onNavigate }: SidebarProps) {
  const { pathname } = useLocation();
  const host = (() => {
    try {
      return new URL(appwriteConfig.endpoint).host;
    } catch {
      return appwriteConfig.endpoint;
    }
  })();

  return (
    <nav
      id="ela-sidebar"
      aria-label="Primary"
      className={clsx(styles.sidebar, open && styles.open)}
    >
      <div className={styles.brand}>
        <img className={styles.mark} src="/ela-logo.svg" alt="ELA Lab" />
        <span className={styles.wordmark}>
          <span className={styles.wordmarkTop}>ela</span>
          <span className={styles.wordmarkBottom}>Lab</span>
        </span>
      </div>

      {moduleSections.map((section) => {
        const modules = appModules.filter((module) => module.section === section.id);
        if (modules.length === 0) return null;

        return (
          <div key={section.id} className={styles.section}>
            <span className={styles.sectionLabel}>{section.label}</span>
            {modules.map((module) => (
              <NavLink
                key={module.id}
                to={module.path}
                onClick={onNavigate}
                className={clsx(styles.item, isActive(module, pathname) && styles.itemActive)}
                aria-current={isActive(module, pathname) ? 'page' : undefined}
              >
                <Icon name={module.icon} size={17} className={styles.itemIcon} />
                <span className={styles.itemLabel}>{module.label}</span>
                {module.available ? null : <span className={styles.soon}>Soon</span>}
              </NavLink>
            ))}
          </div>
        );
      })}

      <div className={styles.footer}>
        <div>Connected to</div>
        <div className={styles.endpoint}>{host}</div>
      </div>
    </nav>
  );
}
